import { useEffect, useRef } from 'react';

const CITIES = [
  { lat: 28.6, lng: 77.2, size: 1.3 },
  { lat: 19.0, lng: 72.8, size: 1.2 },
  { lat: 40.7, lng: -74.0, size: 1.2 },
  { lat: 51.5, lng: -0.1, size: 1.1 },
  { lat: 48.9, lng: 2.3, size: 1.0 },
  { lat: 35.7, lng: 139.7, size: 1.2 },
  { lat: 31.2, lng: 121.5, size: 1.2 },
  { lat: 1.3, lng: 103.8, size: 1.0 },
  { lat: -33.9, lng: 151.2, size: 1.0 },
  { lat: -23.5, lng: -46.6, size: 1.0 },
  { lat: 55.8, lng: 37.6, size: 1.0 },
  { lat: 22.3, lng: 114.2, size: 1.0 },
  { lat: -1.3, lng: 36.8, size: 0.9 },
  { lat: 25.2, lng: 55.3, size: 1.0 },
];

const CONNECTIONS = [
  [0, 3], [0, 6], [0, 7], [1, 13], [1, 7],
  [2, 3], [2, 5], [2, 9], [3, 4], [3, 10],
  [4, 12], [5, 6], [5, 11], [6, 7], [7, 8],
  [9, 2], [10, 6], [11, 7], [12, 13], [13, 0],
];

export default function CinematicEarthAnimation() {
  const canvasRef = useRef(null);
  const animRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    let w, h;
    const resize = () => {
      const rect = canvas.parentElement.getBoundingClientRect();
      w = rect.width;
      h = rect.height;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = w + 'px';
      canvas.style.height = h + 'px';
    };
    resize();
    window.addEventListener('resize', resize);

    // Load earth texture
    const earthImg = new Image();
    earthImg.crossOrigin = 'anonymous';
    earthImg.src = '/images/earth-texture.jpg';
    let textureReady = false;
    earthImg.onload = () => { textureReady = true; };

    // Stars
    const stars = Array.from({ length: 130 }, () => ({
      x: Math.random(), y: Math.random(),
      r: Math.random() * 1.2 + 0.2,
      phase: Math.random() * Math.PI * 2,
      speed: 0.4 + Math.random() * 1.2,
    }));

    // Pulse travelers
    const pulses = CONNECTIONS.map(() => ({
      progress: Math.random(),
      speed: 0.06 + Math.random() * 0.1,
      size: 2 + Math.random() * 1.5,
      direction: Math.random() > 0.5 ? 1 : -1,
    }));

    function project(lat, lng, rotation, cx, cy, R) {
      const phi = (90 - lat) * (Math.PI / 180);
      const theta = (lng + 180 + rotation) * (Math.PI / 180);
      const x3d = -Math.sin(phi) * Math.cos(theta);
      const y3d = Math.cos(phi);
      const z3d = Math.sin(phi) * Math.sin(theta);
      return { x: cx + x3d * R, y: cy - y3d * R, z: z3d };
    }

    function getArcCP(a, b, cx, cy, bulge) {
      const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2;
      const dx = mx - cx, dy = my - cy;
      const d = Math.sqrt(dx * dx + dy * dy) || 1;
      return { x: mx + (dx / d) * bulge, y: my + (dy / d) * bulge };
    }

    function quadPt(a, cp, b, t) {
      const u = 1 - t;
      return {
        x: u * u * a.x + 2 * u * t * cp.x + t * t * b.x,
        y: u * u * a.y + 2 * u * t * cp.y + t * t * b.y,
      };
    }

    // Draw textured sphere using vertical strip projection
    function drawTexturedSphere(cx, cy, R, rotation) {
      if (!textureReady) return;
      const imgW = earthImg.width;
      const imgH = earthImg.height;

      ctx.save();
      ctx.beginPath();
      ctx.arc(cx, cy, R, 0, Math.PI * 2);
      ctx.clip();

      // Number of vertical strips for smooth curvature
      const strips = 48;
      const rotOffset = ((rotation % 360) / 360) * imgW;

      for (let i = 0; i < strips; i++) {
        const angle0 = (i / strips - 0.5) * Math.PI;
        const angle1 = ((i + 1) / strips - 0.5) * Math.PI;

        const x0 = cx + Math.sin(angle0) * R;
        const x1 = cx + Math.sin(angle1) * R;
        const stripW = x1 - x0;
        if (stripW < 0.5) continue;

        // Source X in texture (with rotation offset)
        const srcX0 = ((i / strips) * imgW + rotOffset) % imgW;
        const srcW = imgW / strips;

        // Vertical scale for curvature (wider at equator, narrower at poles)
        const midAngle = (angle0 + angle1) / 2;
        const cosA = Math.cos(midAngle);

        // Brightness falloff at edges (Lambertian)
        const brightness = 0.25 + cosA * 0.75;

        try {
          // Handle wrap-around at texture edge
          if (srcX0 + srcW <= imgW) {
            ctx.drawImage(earthImg, srcX0, 0, srcW, imgH, x0, cy - R, stripW, R * 2);
          } else {
            const part1W = imgW - srcX0;
            const part1ScreenW = stripW * (part1W / srcW);
            ctx.drawImage(earthImg, srcX0, 0, part1W, imgH, x0, cy - R, part1ScreenW, R * 2);
            ctx.drawImage(earthImg, 0, 0, srcW - part1W, imgH, x0 + part1ScreenW, cy - R, stripW - part1ScreenW, R * 2);
          }
        } catch (e) {
          // Silently handle any drawImage errors
        }

        // Darken edges for 3D curvature
        if (brightness < 0.9) {
          ctx.fillStyle = `rgba(0,0,0,${1 - brightness})`;
          ctx.fillRect(x0, cy - R, stripW, R * 2);
        }
      }

      // Specular highlight (top-left)
      const hlGrad = ctx.createRadialGradient(cx - R * 0.32, cy - R * 0.35, 0, cx, cy, R);
      hlGrad.addColorStop(0, 'rgba(255,255,255,0.10)');
      hlGrad.addColorStop(0.3, 'rgba(255,255,255,0.03)');
      hlGrad.addColorStop(0.6, 'rgba(0,0,0,0)');
      hlGrad.addColorStop(1, 'rgba(0,0,0,0.18)');
      ctx.fillStyle = hlGrad;
      ctx.fillRect(cx - R, cy - R, R * 2, R * 2);

      // Green-teal tint overlay for eco-friendly tone
      const greenTint = ctx.createRadialGradient(cx, cy, R * 0.2, cx, cy, R);
      greenTint.addColorStop(0, 'rgba(16, 185, 129, 0.04)');
      greenTint.addColorStop(0.6, 'rgba(13, 148, 136, 0.06)');
      greenTint.addColorStop(1, 'rgba(6, 95, 70, 0.08)');
      ctx.fillStyle = greenTint;
      ctx.fillRect(cx - R, cy - R, R * 2, R * 2);

      ctx.restore();
    }

    const t0 = performance.now();

    function draw(ts) {
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);

      const time = (ts - t0) / 1000;
      const cx = w / 2;
      const cy = h / 2;
      const R = Math.min(w, h) * 0.37;
      const rotation = time * 4;

      // ── Stars ──
      stars.forEach(s => {
        const alpha = 0.2 + Math.sin(time * s.speed + s.phase) * 0.2;
        ctx.fillStyle = `rgba(200,220,255,${alpha})`;
        ctx.beginPath();
        ctx.arc(s.x * w, s.y * h, s.r, 0, Math.PI * 2);
        ctx.fill();
      });

      // ── Atmosphere glow ──
      for (let i = 3; i >= 1; i--) {
        const gR = R * (1 + i * 0.1);
        const grad = ctx.createRadialGradient(cx, cy, R * 0.92, cx, cy, gR);
        const a = 0.03 + Math.sin(time * 0.3 + i) * 0.01;
        grad.addColorStop(0, i === 1 ? `rgba(16,185,129,${a * 2.5})` : `rgba(20,100,160,${a})`);
        grad.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(cx, cy, gR, 0, Math.PI * 2);
        ctx.fill();
      }

      // ── Textured Earth ──
      drawTexturedSphere(cx, cy, R, rotation);

      // ── Green rim ──
      ctx.strokeStyle = 'rgba(16,185,129,0.1)';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(cx, cy, R, 0, Math.PI * 2);
      ctx.stroke();

      // ── Network arcs & pulses ──
      CONNECTIONS.forEach(([fi, ti], ci) => {
        const a = project(CITIES[fi].lat, CITIES[fi].lng, rotation, cx, cy, R);
        const b = project(CITIES[ti].lat, CITIES[ti].lng, rotation, cx, cy, R);
        if (a.z < 0.05 && b.z < 0.05) return;
        const avgZ = Math.max(a.z, b.z);
        const bulge = 10 + Math.sqrt((a.x - b.x) ** 2 + (a.y - b.y) ** 2) * 0.12;
        const cp = getArcCP(a, b, cx, cy, bulge);

        const arcA = Math.min(avgZ * 0.45, 0.45);
        ctx.strokeStyle = `rgba(52,211,153,${arcA})`;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.quadraticCurveTo(cp.x, cp.y, b.x, b.y);
        ctx.stroke();

        // Pulse
        const p = pulses[ci];
        p.progress += p.speed * 0.016 * p.direction;
        if (p.progress > 1) { p.progress = 0; p.direction = 1; }
        if (p.progress < 0) { p.progress = 1; p.direction = -1; }
        const pt = quadPt(a, cp, b, p.progress);
        const pA = arcA * 1.6;

        const pGrad = ctx.createRadialGradient(pt.x, pt.y, 0, pt.x, pt.y, p.size * 6);
        pGrad.addColorStop(0, `rgba(110,231,183,${pA})`);
        pGrad.addColorStop(0.25, `rgba(52,211,153,${pA * 0.5})`);
        pGrad.addColorStop(0.5, `rgba(16,185,129,${pA * 0.15})`);
        pGrad.addColorStop(1, 'rgba(16,185,129,0)');
        ctx.fillStyle = pGrad;
        ctx.beginPath();
        ctx.arc(pt.x, pt.y, p.size * 6, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = `rgba(255,255,255,${pA})`;
        ctx.beginPath();
        ctx.arc(pt.x, pt.y, p.size * 0.6, 0, Math.PI * 2);
        ctx.fill();
      });

      // ── City nodes ──
      CITIES.forEach((city, i) => {
        const p = project(city.lat, city.lng, rotation, cx, cy, R);
        if (p.z < 0.05) return;
        const alpha = p.z;
        const bR = 1.8 * city.size + Math.sin(time * 2.5 + i) * 0.5;

        const nGrad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, bR * 5);
        nGrad.addColorStop(0, `rgba(52,211,153,${alpha * 0.5})`);
        nGrad.addColorStop(0.4, `rgba(16,185,129,${alpha * 0.15})`);
        nGrad.addColorStop(1, 'rgba(16,185,129,0)');
        ctx.fillStyle = nGrad;
        ctx.beginPath();
        ctx.arc(p.x, p.y, bR * 5, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = `rgba(255,255,255,${alpha * 0.85})`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, bR, 0, Math.PI * 2);
        ctx.fill();
      });

      // ── Pulse rings ──
      for (let r = 0; r < 2; r++) {
        const phase = ((time * 0.22 + r * 0.5) % 1);
        const ringR = R * (1.02 + phase * 0.3);
        const ringA = (1 - phase) * 0.08;
        ctx.strokeStyle = `rgba(16,185,129,${ringA})`;
        ctx.lineWidth = 0.7;
        ctx.beginPath();
        ctx.arc(cx, cy, ringR, 0, Math.PI * 2);
        ctx.stroke();
      }

      animRef.current = requestAnimationFrame(draw);
    }

    animRef.current = requestAnimationFrame(draw);

    return () => {
      window.removeEventListener('resize', resize);
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, []);

  return (
    <div className="w-full h-full relative" style={{ minHeight: '400px' }} data-testid="cinematic-earth-animation">
      <canvas ref={canvasRef} className="w-full h-full" />
    </div>
  );
}
